import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CFormCheck,
  CPagination,
  CPaginationItem,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react-pro";
import type { TradingStrategyDto } from "../../../api/hooks/useTradingStrategies";
import { StrategyInstruments } from "./StrategyInstruments";
import { StrategySideLabel } from "../../trading-strategies/components/StrategySideLabel";

const PAGE_SIZE = 10;

interface FollowTableProps {
  strategies: TradingStrategyDto[];
  /** With these, each row has a tick box and clicking anywhere on the row ticks it. */
  ticked?: ReadonlySet<string>;
  onToggle?: (strategyId: string) => void;
}

/**
 * Strategies one a row, ten a page: what each charts, what it trades and which way. Below tablet width the
 * instruments and direction move under the name, so the table fits a phone without scrolling sideways.
 */
export function FollowTable({ strategies, ticked, onToggle }: FollowTableProps) {
  const { t } = useTranslation(["signals", "tradingStrategies"]);
  const [page, setPage] = useState(1);

  const pages = Math.max(1, Math.ceil(strategies.length / PAGE_SIZE));
  // Ticking under a Ticked or Not ticked filter can leave fewer pages than the one being shown.
  const current = Math.min(page, pages);
  const shown = strategies.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const from = (current - 1) * PAGE_SIZE + 1;
  const to = Math.min(strategies.length, current * PAGE_SIZE);

  return (
    <div>
      <CTable align="middle" hover={onToggle !== undefined} responsive className="mb-2">
        <CTableHead>
          <CTableRow>
            {onToggle && <CTableHeaderCell scope="col" style={{ width: "2.5rem" }} />}
            <CTableHeaderCell scope="col">{t("configure.table.strategy")}</CTableHeaderCell>
            <CTableHeaderCell scope="col" className="d-none d-md-table-cell">
              {t("configure.table.instruments")}
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" className="text-center d-none d-md-table-cell">
              {t("configure.table.sides")}
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {shown.map((strategy) => {
            const direction = strategy.direction;
            const name = direction
              ? `${strategy.publicName} (${t(`tradingStrategies:sides.${direction}`)})`
              : strategy.publicName;

            return (
              <CTableRow
                key={strategy.id}
                onClick={onToggle ? () => onToggle(strategy.id) : undefined}
                style={onToggle ? { cursor: "pointer" } : undefined}
              >
                {onToggle && (
                  <CTableDataCell>
                    <CFormCheck
                      id={`follow-${strategy.id}`}
                      className="m-0"
                      checked={ticked?.has(strategy.id) ?? false}
                      aria-label={t("configure.table.tick", { name })}
                      onChange={() => onToggle(strategy.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </CTableDataCell>
                )}
                <CTableDataCell>
                  <div className="fw-semibold">{strategy.publicName}</div>
                  <div className="d-flex d-md-none align-items-center gap-2 mt-1">
                    <StrategyInstruments strategy={strategy} compact />
                    <StrategySideLabel direction={direction} className="small" />
                  </div>
                </CTableDataCell>
                <CTableDataCell className="d-none d-md-table-cell">
                  <StrategyInstruments strategy={strategy} />
                </CTableDataCell>
                <CTableDataCell className="text-center d-none d-md-table-cell">
                  <StrategySideLabel direction={direction} className="small" />
                </CTableDataCell>
              </CTableRow>
            );
          })}
        </CTableBody>
      </CTable>

      {pages > 1 && (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span className="small text-body-secondary">
            {t("configure.table.range", { from, to, total: strategies.length })}
          </span>
          <CPagination aria-label={t("configure.table.pages")} className="mb-0">
            <CPaginationItem
              aria-label={t("configure.table.previous")}
              disabled={current <= 1}
              onClick={() => setPage(current - 1)}
            >
              «
            </CPaginationItem>
            {Array.from({ length: pages }, (_, i) => i + 1).map((number) => (
              <CPaginationItem key={number} active={number === current} onClick={() => setPage(number)}>
                {number}
              </CPaginationItem>
            ))}
            <CPaginationItem
              aria-label={t("configure.table.next")}
              disabled={current >= pages}
              onClick={() => setPage(current + 1)}
            >
              »
            </CPaginationItem>
          </CPagination>
        </div>
      )}
    </div>
  );
}
